import { useEffect } from "react";
import { collectionRef } from "@/api/collectionRef/collectionOperations";
import { useChallengeStore } from "@/store/challenges/challenges.store.v2";
import { Challenge } from "@/models/database/challenges/challenge.model";
import { getAuthorProfileBasedOnUid } from "@/api/search/searchUsernameByName";

export const useTenantZoneChallenges = (tenantId: string, zoneId: string) => {
  const { challenges, loading, error, setChallenges, setLoading, setError } =
    useChallengeStore();

  const fetchZoneChallenges = async () => {
    try {
      setLoading(true);
      const challengesArray: Challenge[] = [];

      const ref = collectionRef.challenge
        .where("client", "==", tenantId)
        .where("status", "==", "ongoing")
        .where("zone", "==", zoneId);
      const query = await ref.get();

      // Get challenges from Firestore
      await Promise.all(
        query.docs.map(async (doc) => {
          const author = await getAuthorProfileBasedOnUid(
            doc.data().createdBy,
            doc.data().client
          );
          const challengeData = { author: author, id: doc.id, ...doc.data() };
          challengesArray.push(challengeData as Challenge);
          return challengeData;
        })
      );

      const updatedChallenges = [...(challenges || []), ...challengesArray];
      // Remove duplicates based on challenge ID
      const uniqueChallenges = Array.from(
        new Map(
          updatedChallenges.map((challenge) => [challenge.slug, challenge])
        ).values()
      );

      setChallenges(uniqueChallenges);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch challenges
    if (!challenges) {
      fetchZoneChallenges();
    }
  }, []);

  useEffect(() => {
    // Fetch challenges if zoneId changes
    if (zoneId) {
      fetchZoneChallenges();
    }
  }, [zoneId]);

  const filteredByZone = challenges?.filter(
    (challenge) => challenge.zone === zoneId
  );

  return {
    data: filteredByZone,
    loading,
    error,
  };
};
